import {
  Box,
  Button,
  Flex,
  Spinner,
  Stack,
  Text,
  useToken,
} from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import OtpStep from '../../modules/Settings/Security/TwoFactorAuth/shared/OtpStep';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { TMfaActionType } from '../../store/slices/security/mfaTypes';
import {
  resendMfaOtpAsync,
  resetResendMfaOtp,
} from '../../store/slices/security/resendMfaOtpSlice';
import {
  checkTransaction2faAsync,
  resetCheckTransaction2fa,
} from '../../store/slices/transaction2fa/checkTransaction2faSlice';
import { TTransaction2faType } from '../../store/slices/transaction2fa/transaction2faTypes';
import {
  resetVerifyTransaction2faToken,
  verifyTransaction2faTokenAsync,
} from '../../store/slices/transaction2fa/verifyTransaction2faTokenSlice';
import { PrimaryButton, SecondaryButton } from '../custom';

interface Transaction2faStepProps {
  type: TTransaction2faType;
  amountKobo: number;
  onProceed: () => void;
  onBack?: () => void;
}

export default function Transaction2faStep({
  type,
  amountKobo,
  onProceed,
  onBack,
}: Transaction2faStepProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const checkResult = useAppSelector((state) => state.checkTransaction2fa);
  const verifyResult = useAppSelector(
    (state) => state.verifyTransaction2faToken,
  );
  const resendMfaOtp = useAppSelector((state) => state.resendMfaOtp);

  const checkData = checkResult.value?.data;
  const proceededRef = useRef(false);

  useEffect(() => {
    dispatch(checkTransaction2faAsync({ type, amount: amountKobo }));
    return () => {
      dispatch(resetCheckTransaction2fa());
      dispatch(resetVerifyTransaction2faToken());
      dispatch(resetResendMfaOtp());
    };
  }, [dispatch, type, amountKobo]);

  useEffect(() => {
    if (proceededRef.current) return;
    if (
      checkResult.status === 'success' &&
      checkData &&
      !checkData.mfaRequired &&
      !checkData.requireSetup
    ) {
      proceededRef.current = true;
      onProceed();
    }
  }, [checkResult.status, checkData, onProceed]);

  const handleVerify = async (token: string) => {
    await dispatch(verifyTransaction2faTokenAsync({ token })).unwrap();
    onProceed();
  };

  const handleResend = async () => {
    const actionType = (checkData?.actionType ?? null) as TMfaActionType;
    if (!actionType) return;
    await dispatch(resendMfaOtpAsync({ actionType }));
  };

  const handleGoToSecurity = () => {
    navigate('/app/settings');
  };

  if (checkResult.status === 'loading' || checkResult.status === 'idle') {
    return (
      <Flex justify="center" align="center" py={10}>
        <Spinner color="pMalachite.600" />
      </Flex>
    );
  }

  if (checkResult.status === 'failed') {
    return (
      <Stack spacing={4} py={6}>
        <Text fontWeight={500}>We couldn&apos;t check 2FA right now.</Text>
        <Text color="gray.300" fontSize=".875rem">
          {checkResult.value?.message ??
            'Please try again. If the problem persists, contact support.'}
        </Text>
        <Stack direction="row" spacing={3}>
          {onBack && (
            <SecondaryButton type="button" onClick={onBack}>
              Back
            </SecondaryButton>
          )}
          <PrimaryButton
            type="button"
            onClick={() =>
              dispatch(checkTransaction2faAsync({ type, amount: amountKobo }))
            }
          >
            Try again
          </PrimaryButton>
        </Stack>
      </Stack>
    );
  }

  if (checkData?.requireSetup) {
    return <SetupRequiredBlock message={checkData.message} onGoToSecurity={handleGoToSecurity} onBack={onBack} />;
  }

  if (checkData?.mfaRequired && checkData.type) {
    return (
      <Stack spacing={5} py={2}>
        <Stack spacing={1}>
          <Text fontWeight={500}>Two-factor authentication</Text>
          {checkData.message ? (
            <Text color="gray.300" fontSize=".875rem">
              {checkData.message}
            </Text>
          ) : null}
        </Stack>
        <OtpStep
          channel={checkData.type}
          loading={verifyResult.status === 'loading'}
          resending={resendMfaOtp.status === 'loading'}
          onSubmit={handleVerify}
          onResend={checkData.actionType ? handleResend : undefined}
          submitLabel="Continue"
        />
        {onBack && (
          <Flex justify="center">
            <Button variant="link" color="pMalachite.600" onClick={onBack}>
              Back
            </Button>
          </Flex>
        )}
      </Stack>
    );
  }

  return null;
}

interface SetupRequiredBlockProps {
  message?: string;
  onGoToSecurity: () => void;
  onBack?: () => void;
}

function SetupRequiredBlock({
  message,
  onGoToSecurity,
  onBack,
}: SetupRequiredBlockProps) {
  const [orange] = useToken('colors', ['orange.50']);
  return (
    <Stack spacing={5} py={4}>
      <Box bg={orange ?? '#FFF8EB'} p={4} borderRadius="12px">
        <Text fontWeight={500} mb={1}>
          Set up 2FA to continue
        </Text>
        <Text color="gray.300" fontSize=".875rem">
          {message ??
            '2FA is required to complete this transaction. Set it up in Security Settings and try again.'}
        </Text>
      </Box>
      <Stack direction="row" spacing={3}>
        {onBack && (
          <SecondaryButton type="button" onClick={onBack}>
            Back
          </SecondaryButton>
        )}
        <PrimaryButton type="button" onClick={onGoToSecurity}>
          Go to Security Settings
        </PrimaryButton>
      </Stack>
    </Stack>
  );
}
